import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Building2,
  CheckCircle2,
  CircleDollarSign,
  FileDown,
  HandCoins,
  Loader2,
  Mail,
  TrendingUp,
  Undo2,
  Wallet,
} from "lucide-react";
import { api } from "@/lib/api";
import { displayName } from "./shared";

const brl = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const nextFridayLabel = () => {
  const d = new Date();
  const add = (5 - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + add);
  const label = d.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" });
  return add === 0 ? `hoje, ${label}` : label;
};

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "";

const norm = (s) => (s || "").trim().toLowerCase();

export const FinanceiroView = ({ leads, onChanged }) => {
  const [saving, setSaving] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [exporting, setExporting] = useState(false);

  const exportReport = async () => {
    setExporting(true);
    const fetchPdf = () =>
      api.get(`/admin/finance/report?month=${month}`, { responseType: "blob" });
    try {
      let res;
      try {
        res = await fetchPdf();
      } catch (e) {
        if (e.response?.status === 401) {
          await api.post("/auth/refresh");
          res = await fetchPdf();
        } else {
          throw e;
        }
      }
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `relatorio-financeiro-${month}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Falha ao gerar o relatório.");
    } finally {
      setExporting(false);
    }
  };

  const [sendingSummary, setSendingSummary] = useState(false);
  const sendSummary = async () => {
    setSendingSummary(true);
    try {
      const { data } = await api.post("/admin/finance/friday-summary");
      toast.success(`Resumo de sexta-feira enviado para ${data.to}.`);
    } catch (e) {
      toast.error(
        typeof e.response?.data?.detail === "string"
          ? e.response.data.detail
          : "Falha ao enviar o resumo por e-mail."
      );
    } finally {
      setSendingSummary(false);
    }
  };

  const done = useMemo(
    () =>
      leads.filter(
        (l) =>
          l.type === "service" &&
          l.status === "concluido" &&
          l.payload?.pricing?.final_price != null
      ),
    [leads]
  );

  const partnerAgencies = useMemo(
    () =>
      new Set(
        leads
          .filter((l) => l.type === "agency" && l.status === "parceria_fechada")
          .map((l) => norm(displayName(l)))
      ),
    [leads]
  );

  const agencyPct = useMemo(() => {
    const m = new Map();
    leads
      .filter((l) => l.type === "agency" && l.status === "parceria_fechada")
      .forEach((l) => m.set(norm(displayName(l)), l.payload?.cashback_pct ?? 10));
    return m;
  }, [leads]);

  const fin = (l) => l.payload?.finance || {};
  const profit = (l) =>
    (l.payload?.pricing?.final_price || 0) - (l.payload?.pricing?.provider_price || 0);
  const cashbackPct = (l) =>
    fin(l).cashback_pct ?? agencyPct.get(norm(l.payload?.agency_name)) ?? 10;
  const cashbackValue = (l) => (profit(l) * cashbackPct(l)) / 100;

  const aReceber = done.filter((l) => !fin(l).received_at);
  const recebidos = done.filter((l) => fin(l).received_at);
  const aPagar = done.filter(
    (l) => l.payload?.evaluation && l.payload?.pricing?.provider_price != null && !fin(l).paid_provider_at
  );
  const pagos = done.filter((l) => fin(l).paid_provider_at);
  const aguardandoAprovacao = done.filter((l) => !l.payload?.evaluation);
  const cashbackRows = done.filter((l) => l.payload?.agency_name);
  const cashbackPendente = cashbackRows.filter((l) => !fin(l).cashback_paid_at);

  const totalReceber = aReceber.reduce((s, l) => s + l.payload.pricing.final_price, 0);
  const totalPagar = aPagar.reduce((s, l) => s + (l.payload.pricing.provider_price || 0), 0);
  const totalCashback = cashbackPendente.reduce((s, l) => s + cashbackValue(l), 0);
  const lucroPrevisto =
    done.reduce((s, l) => s + l.payload.pricing.final_price, 0) -
    done.reduce((s, l) => s + (l.payload.pricing.provider_price || 0), 0) -
    cashbackRows.reduce((s, l) => s + cashbackValue(l), 0);

  const setFinance = async (lead, patch, okMsg) => {
    setSaving(lead.id);
    try {
      await api.patch(`/admin/leads/${lead.id}`, { finance: patch });
      toast.success(okMsg);
      onChanged();
    } catch {
      toast.error("Falha ao atualizar o financeiro.");
    } finally {
      setSaving(null);
    }
  };

  const savePct = async (lead, value) => {
    const pct = parseFloat(String(value).replace(",", "."));
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      toast.error("Percentual inválido (0 a 100).");
      return;
    }
    await setFinance(lead, { cashback_pct: pct }, "Cashback atualizado.");
  };

  const StatCard = ({ icon: Icon, label, value, testId, tone = "text-petrol" }) => (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="flex items-center gap-2 text-slate-400">
        <Icon className="h-4 w-4" />
        <p className="text-xs font-medium">{label}</p>
      </div>
      <p className={`mt-2 font-display text-2xl font-extrabold ${tone}`}>{brl.format(value)}</p>
    </div>
  );

  const ActionButton = ({ lead, onClick, testId, children, tone = "bg-petrol hover:bg-brand" }) => (
    <button
      onClick={onClick}
      disabled={saving === lead.id}
      data-testid={testId}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 text-[0.65rem] font-semibold text-white transition-colors disabled:opacity-60 ${tone}`}
    >
      {saving === lead.id ? <Loader2 className="h-3 w-3 animate-spin" /> : children}
    </button>
  );

  return (
    <div data-testid="financeiro-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-petrol sm:text-3xl">Financeiro</h1>
          <p className="mt-1 text-sm text-slate-500">
            Contas a receber dos clientes, pagamentos aos prestadores (toda sexta-feira, após
            serviço finalizado e aprovado) e cashback das imobiliárias parceiras.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            data-testid="fin-report-month"
            className="h-9 rounded-full border border-slate-200 bg-white px-4 text-xs font-medium text-petrol outline-none transition-colors focus:border-brand"
          />
          <button
            onClick={exportReport}
            disabled={exporting}
            data-testid="fin-export-button"
            className="inline-flex h-9 items-center gap-2 rounded-full bg-petrol px-4 text-xs font-semibold text-white transition-colors hover:bg-brand disabled:opacity-60"
          >
            {exporting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <FileDown className="h-3.5 w-3.5" />
            )}
            Exportar PDF
          </button>
          <button
            onClick={sendSummary}
            disabled={sendingSummary}
            data-testid="fin-send-summary-button"
            title="Envia agora, por e-mail, o resumo dos pagamentos de prestadores da sexta-feira"
            className="inline-flex h-9 items-center gap-2 rounded-full border border-petrol/20 px-4 text-xs font-semibold text-petrol transition-colors hover:border-brand hover:text-brand disabled:opacity-60"
          >
            {sendingSummary ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Mail className="h-3.5 w-3.5" />
            )}
            Resumo de sexta
          </button>
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="financeiro-stats">
        <StatCard icon={Wallet} label="A receber de clientes" value={totalReceber} testId="fin-stat-receber" />
        <StatCard
          icon={HandCoins}
          label={`A pagar na sexta (${nextFridayLabel()})`}
          value={totalPagar}
          testId="fin-stat-pagar"
          tone="text-red-600"
        />
        <StatCard
          icon={Building2}
          label="Cashback a pagar"
          value={totalCashback}
          testId="fin-stat-cashback"
          tone="text-violet-600"
        />
        <StatCard
          icon={TrendingUp}
          label="Lucro previsto (concluídos)"
          value={lucroPrevisto}
          testId="fin-stat-lucro"
          tone="text-emerald-600"
        />
      </div>

      {/* A RECEBER */}
      <section className="mt-8" data-testid="fin-receber-section">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-lg font-bold text-petrol">A receber (clientes)</h2>
          <p className="text-xs text-slate-400">
            Serviços concluídos com orçamento fechado e ainda não recebidos.
          </p>
        </div>
        <div className="mt-3 space-y-2">
          {aReceber.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              Nada a receber no momento.
            </p>
          ) : (
            aReceber.map((l) => (
              <div
                key={l.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3.5"
                data-testid={`fin-receber-${l.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-petrol">{displayName(l)}</p>
                  <p className="mt-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                    {l.payload?.service_type || "Serviço"} · {l.payload?.city}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-display text-base font-bold text-petrol">
                    {brl.format(l.payload.pricing.final_price)}
                  </p>
                  <ActionButton
                    lead={l}
                    testId={`fin-receber-ok-${l.id}`}
                    onClick={() => setFinance(l, { received: true }, "Recebimento registrado.")}
                  >
                    <CheckCircle2 className="h-3 w-3" /> Marcar recebido
                  </ActionButton>
                </div>
              </div>
            ))
          )}
        </div>
        {recebidos.length > 0 && (
          <ul className="mt-2 space-y-1.5" data-testid="fin-recebidos-list">
            {recebidos.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50/60 px-4 py-2 text-xs text-slate-500"
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <span className="truncate">
                    {displayName(l)} — {brl.format(l.payload.pricing.final_price)} recebidos em{" "}
                    {fmtDateTime(fin(l).received_at)}
                  </span>
                </span>
                <button
                  onClick={() => setFinance(l, { received: false }, "Recebimento desfeito.")}
                  data-testid={`fin-receber-undo-${l.id}`}
                  className="inline-flex shrink-0 items-center gap-1 text-[0.65rem] font-semibold text-slate-400 hover:text-red-600"
                >
                  <Undo2 className="h-3 w-3" /> desfazer
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* A PAGAR — PRESTADORES */}
      <section className="mt-8" data-testid="fin-pagar-section">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-lg font-bold text-petrol">
            A pagar — prestadores (sexta-feira)
          </h2>
          <p className="text-xs text-slate-400">
            Liberado quando o serviço está concluído e aprovado (avaliação registrada).
          </p>
        </div>
        <div className="mt-3 space-y-2">
          {aPagar.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              Nenhum pagamento liberado para esta sexta-feira.
            </p>
          ) : (
            aPagar.map((l) => (
              <div
                key={l.id}
                className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-3.5"
                data-testid={`fin-pagar-${l.id}`}
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-petrol">
                    {l.payload?.indicated_partner?.name || "Prestador"}
                  </p>
                  <p className="mt-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                    {l.payload?.service_type || "Serviço"} · {displayName(l)} · aprovado{" "}
                    {fmtDateTime(l.payload?.evaluation?.evaluated_at)}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <p className="font-display text-base font-bold text-red-600">
                    {brl.format(l.payload.pricing.provider_price || 0)}
                  </p>
                  <ActionButton
                    lead={l}
                    testId={`fin-pagar-ok-${l.id}`}
                    tone="bg-red-600 hover:bg-red-700"
                    onClick={() => setFinance(l, { paid_provider: true }, "Pagamento registrado.")}
                  >
                    <CheckCircle2 className="h-3 w-3" /> Marcar pago
                  </ActionButton>
                </div>
              </div>
            ))
          )}
        </div>
        {aguardandoAprovacao.length > 0 && (
          <p className="mt-2 text-xs text-amber-600" data-testid="fin-aguardando-aprovacao">
            {aguardandoAprovacao.length} serviço(s) concluído(s) aguardando avaliação para liberar o
            pagamento: {aguardandoAprovacao.map((l) => displayName(l)).join(", ")}.
          </p>
        )}
        {pagos.length > 0 && (
          <ul className="mt-2 space-y-1.5" data-testid="fin-pagos-list">
            {pagos.map((l) => (
              <li
                key={l.id}
                className="flex items-center justify-between gap-3 rounded-xl bg-emerald-50/60 px-4 py-2 text-xs text-slate-500"
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-600" />
                  <span className="truncate">
                    {l.payload?.indicated_partner?.name || "Prestador"} —{" "}
                    {brl.format(l.payload?.pricing?.provider_price || 0)} pagos em{" "}
                    {fmtDateTime(fin(l).paid_provider_at)}
                  </span>
                </span>
                <button
                  onClick={() => setFinance(l, { paid_provider: false }, "Pagamento desfeito.")}
                  data-testid={`fin-pagar-undo-${l.id}`}
                  className="inline-flex shrink-0 items-center gap-1 text-[0.65rem] font-semibold text-slate-400 hover:text-red-600"
                >
                  <Undo2 className="h-3 w-3" /> desfazer
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* CASHBACK IMOBILIÁRIAS */}
      <section className="mt-8" data-testid="fin-cashback-section">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h2 className="font-display text-lg font-bold text-petrol">Cashback — imobiliárias</h2>
          <p className="text-xs text-slate-400">
            Percentual sobre o lucro da Resolv (valor final − valor do prestador). Padrão: 10%,
            editável por serviço.
          </p>
        </div>
        <div className="mt-3 space-y-2">
          {cashbackRows.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-slate-300 bg-white/60 px-5 py-8 text-center text-sm text-slate-400">
              Nenhum serviço concluído vinculado a imobiliária ainda. Locatários informam a
              imobiliária ao abrir a solicitação.
            </p>
          ) : (
            cashbackRows.map((l) => {
              const isPartner = partnerAgencies.has(norm(l.payload?.agency_name));
              const paid = Boolean(fin(l).cashback_paid_at);
              return (
                <div
                  key={l.id}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-5 py-3.5 ${
                    paid ? "border-emerald-100 bg-emerald-50/50" : "border-slate-200 bg-white"
                  }`}
                  data-testid={`fin-cashback-${l.id}`}
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-semibold text-petrol">
                      <span className="truncate">{l.payload?.agency_name}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[0.6rem] font-semibold ${
                          isPartner
                            ? "bg-violet-100 text-violet-600"
                            : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {isPartner ? "parceira" : "não cadastrada"}
                      </span>
                    </p>
                    <p className="mt-0.5 font-mono text-[0.65rem] uppercase tracking-wider text-slate-400">
                      {displayName(l)} · lucro {brl.format(profit(l))}
                      {paid && ` · pago em ${fmtDateTime(fin(l).cashback_paid_at)}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="flex items-center gap-1.5 text-[0.65rem] font-semibold text-slate-400">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        defaultValue={cashbackPct(l)}
                        key={`${l.id}-${cashbackPct(l)}`}
                        disabled={paid}
                        onBlur={(e) => {
                          const v = parseFloat(String(e.target.value).replace(",", "."));
                          if (!Number.isNaN(v) && v !== cashbackPct(l)) savePct(l, e.target.value);
                        }}
                        data-testid={`fin-cashback-pct-${l.id}`}
                        className="h-8 w-16 rounded-lg border border-slate-200 bg-white px-2 text-center text-xs text-petrol outline-none focus:border-brand disabled:opacity-50"
                      />
                      %
                    </label>
                    <p className="font-display text-base font-bold text-violet-600">
                      {brl.format(cashbackValue(l))}
                    </p>
                    {!paid ? (
                      <ActionButton
                        lead={l}
                        testId={`fin-cashback-ok-${l.id}`}
                        tone="bg-violet-600 hover:bg-violet-700"
                        onClick={() =>
                          setFinance(l, { cashback_paid: true }, "Cashback registrado como pago.")
                        }
                      >
                        <CheckCircle2 className="h-3 w-3" /> Marcar pago
                      </ActionButton>
                    ) : (
                      <ActionButton
                        lead={l}
                        testId={`fin-cashback-undo-${l.id}`}
                        tone="bg-slate-400 hover:bg-slate-500"
                        onClick={() =>
                          setFinance(l, { cashback_paid: false }, "Pagamento de cashback desfeito.")
                        }
                      >
                        <Undo2 className="h-3 w-3" /> desfazer
                      </ActionButton>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </section>

      <p className="mt-6 flex items-center gap-2 text-[0.65rem] text-slate-400">
        <CircleDollarSign className="h-3.5 w-3.5" />
        Prestadores recebem toda sexta-feira, sempre após o serviço finalizado e aprovado pelo
        cliente ou operador.
      </p>
    </div>
  );
};
